import {
  AbTestType,
  BrandFeatureName,
  ClientAppId,
  country,
  CreationData,
  DocumentType,
  DocumentTypeEnum,
  DossierGlobalOrigins,
  FeatureBrandPartnerBranchIdStrategy,
  FeatureName,
  InsuranceSuggestion,
  LicensePlatePattern,
  MapConfig,
  OffsetDurations,
  OperationType,
  OriginFilter,
  PublicConfiguration,
} from '../models';

export abstract class ConfigurationBaseService {
  protected configuration: PublicConfiguration;

  getId(): string {
    return this.configuration?.id;
  }

  getConfig() {
    return this.configuration;
  }

  filterInsuranceSuggestions(
    insuranceSuggestion: InsuranceSuggestion[],
    filter: string
  ): string[] {
    return this.filterSuggestions(
      insuranceSuggestion.map((x) => x.insuranceName),
      filter
    );
  }

  filterSuggestions(suggestionList: string[], filter: string): string[] {
    return filter
      ? suggestionList.filter((suggestion) => {
          const lowerSuggestion = suggestion.toLowerCase();
          const lowerFilter = filter.toLowerCase();
          return (
            lowerSuggestion.startsWith(lowerFilter) ||
            lowerSuggestion.includes(' ' + lowerFilter)
          );
        })
      : suggestionList;
  }

  getSortedInsuranceSuggestions(): InsuranceSuggestion[] {
    return this.configuration?.insuranceSuggestion.sort((a, b) =>
      a.insuranceName.localeCompare(b.insuranceName)
    );
  }

  isFrenchContext() {
    return 'fr' === this.configuration?.map.defaultCountryCode;
  }

  getAllLicensePlateCountries(): country[] {
    return this.configuration?.patterns.licensePlate.map(
      (pattern) => pattern.value
    );
  }

  getDefaultLicensePlateCountry(): country {
    return this.configuration?.patterns.licensePlate[0]?.value;
  }

  getDefaultPhoneNumberCountry(): country {
    return this.configuration?.patterns?.phoneNumber[0];
  }

  getGlassUsage(clientAppId: ClientAppId) {
    return clientAppId === 'insurer' && !!this.configuration?.glassUsageInsurer
      ? this.configuration?.glassUsageInsurer
      : this.configuration?.glassUsage;
  }

  getAllExcess() {
    const excess0 = this.configuration?.excessOptions.excessList.excess0;
    const excessWithValue =
      this.configuration?.excessOptions.excessList.excessWithValue;
    const proportionalExcess =
      this.configuration?.excessOptions.excessList.proportionalExcess;
    return [...excess0, ...excessWithValue, ...proportionalExcess];
  }

  getFitterBrandInfosByBrandId(brandId: string) {
    return this.configuration?.fitterBrands?.find(
      (brand) => brand.id === brandId
    );
  }

  getBrandName(brandId: string) {
    return this.getFitterBrandInfosByBrandId(brandId)?.name ?? '';
  }

  getBrandSlotSearchDateRange(brandId: string) {
    return (
      this.getFitterBrandInfosByBrandId(brandId)?.booking?.search
        ?.rangeByDays ?? 20
    );
  }

  getBrandSearchVersion(brandId: string) {
    return this.getFitterBrandInfosByBrandId(brandId)?.booking?.search?.version;
  }

  getBrandMobilityHalfDayLimit(brandId: string) {
    return (
      this.getFitterBrandInfosByBrandId(brandId)?.mobilityHalfDayLimit ??
      '12:00'
    );
  }

  getBrandDefaultOffset(
    brandId: string,
    operationType: OperationType,
    inMobility: boolean
  ) {
    const brandSearchObject =
      this.getFitterBrandInfosByBrandId(brandId)?.booking?.search;

    const usedOffsetInfos = inMobility
      ? brandSearchObject?.mobilityOffset
      : brandSearchObject?.offset;

    if (operationType === 'Replace') {
      return usedOffsetInfos?.replace ?? 20;
    } else {
      return usedOffsetInfos?.repair ?? 1;
    }
  }
  getBrandDefaultOffsets(brandId: string): {
    center: OffsetDurations;
    mobility: OffsetDurations;
  } {
    const brandSearchObject =
      this.getFitterBrandInfosByBrandId(brandId)?.booking?.search;

    return {
      center: brandSearchObject?.offset,
      mobility: brandSearchObject?.mobilityOffset,
    };
  }

  getDefaultTimezone() {
    return this.configuration?.uniqueTimezone || 'Etc/UTC';
  }

  getSupportedDocumentTypes(): DocumentType[] {
    return (
      this.configuration?.supportedDocuments ||
      (Object.values(DocumentTypeEnum) as unknown as DocumentType[])
    );
  }

  getLogoutDelay(userGroups: string[]) {
    let defaultDelay = 5 * 60; // 5 heures par défaut
    if (this.configuration?.autoLogoutDelay?.length > 0) {
      const conf = this.configuration?.autoLogoutDelay.filter(
        (logoutConfig) => !logoutConfig.group
      );
      if (conf.length > 0) defaultDelay = conf[0].delay;
      const otherDelay = this.configuration?.autoLogoutDelay
        .filter((logoutConfig) => logoutConfig.group)
        .map((logoutConfig) => {
          if (userGroups && userGroups.indexOf(logoutConfig.group) > -1) {
            return logoutConfig.delay;
          } else {
            return defaultDelay;
          }
        });
      if (otherDelay.length > 0) return Math.min(...otherDelay);
    }
    return defaultDelay;
  }

  getOriginFiltersAllowed() {
    let originFilters: DossierGlobalOrigins[] = [];
    this.configuration?.originFilters.forEach((originFilter) => {
      if (!originFilters.includes(originFilter?.lib)) {
        originFilters.push(originFilter?.lib);
      }
    });
    return originFilters;
  }

  getOriginFilterFormCreationData(creationData: CreationData): OriginFilter {
    for (let filter of this.configuration?.originFilters || []) {
      if (
        (filter.origin === creationData?.origin || filter.origin === '*') &&
        (filter.businessOrigin ===
          creationData?.businessOrigin?.split('_')[0] ||
          filter.businessOrigin === '*')
      ) {
        return filter;
      }
    }
    return null;
  }

  getFitterIdStrategy(brandId: string): FeatureBrandPartnerBranchIdStrategy {
    return this.getFitterBrandInfosByBrandId(brandId)?.idStrategy ?? 'FCE';
  }

  isBOSpecificCreds(brandId: string, businessOrigin): boolean {
    return (
      this.getFitterBrandInfosByBrandId(
        brandId
      )?.booking.specificBOCredentials?.indexOf(businessOrigin) > -1
    );
  }

  getBookModifyStrategyByBrandId(brandId: string) {
    return this.getFitterBrandInfosByBrandId(brandId)?.booking?.modify
      ?.strategy;
  }

  getInsurerFeatures(): FeatureName[] {
    return this.configuration?.features ?? [];
  }

  getBrandFeaturesByBrandId(brandId: string): BrandFeatureName[] {
    return this.getFitterBrandInfosByBrandId(brandId)?.features ?? [];
  }

  getMapConfig(): MapConfig {
    return this.configuration?.map;
  }

  getInsurerName(): string {
    return this.configuration?.insurerName;
  }

  getDefaultLang() {
    return this.configuration?.defaultLang;
  }

  getDefaultPhoneNumberPattern() {
    return this.configuration?.patterns.phoneNumber[0];
  }

  getDefaultLicensePlateOrigin(): LicensePlatePattern {
    return this.configuration?.patterns?.licensePlate?.length
      ? this.configuration?.patterns.licensePlate[0]
      : null;
  }

  getBrandInterventionDuration(brandId: string) {
    return (
      this.getFitterBrandInfosByBrandId(brandId)?.averageInterventionDuration ??
      '1h15'
    );
  }

  isSupportedDocumentType(docType: DocumentType) {
    return (
      this.getSupportedDocumentTypes().findIndex(
        (supported) => supported === docType
      ) > -1
    );
  }

  getDossierGlobalOrigin(creationData: {
    origin: string;
    businessOrigin?: string;
  }): OriginFilter {
    for (let filter of this.configuration?.originFilters || []) {
      if (
        (filter.origin === creationData.origin || filter.origin === '*') &&
        (filter.businessOrigin === creationData.businessOrigin?.split('_')[0] ||
          filter.businessOrigin === '*')
      ) {
        return filter;
      }
    }
    return;
  }

  getDossierGlobalOriginFromCreationData(
    creationData: {
      origin: string;
      businessOrigin?: string;
    },
    clientAppId: ClientAppId
  ): DossierGlobalOrigins {
    if (!creationData) {
      return getDefaultOrigin(clientAppId);
    }
    const globalOrigin = this.getDossierGlobalOrigin(creationData);

    if (!globalOrigin) {
      return 'OTHER';
    }

    return globalOrigin.lib;
  }

  hasBrandFeature(feature: BrandFeatureName, brandId: string): boolean {
    const fitterBrand = this.configuration?.fitterBrands?.find(
      (brand) => brand.id === brandId
    );
    return fitterBrand && fitterBrand.features.indexOf(feature) > -1;
  }

  getFirstBrandId(): string {
    if (this.configuration?.fitterBrands.length > 0) {
      return this.configuration?.fitterBrands[0].id;
    } else {
      return null;
    }
  }

  getMasterBrands(): { brandId: string; masterConf: string }[] {
    return (
      this.configuration.fitterBrands
        ?.filter((brand) => !!brand.partners?.import?.masterConf)
        .map((brand) => {
          return {
            brandId: brand.id,
            masterConf: brand.partners?.import?.masterConf,
          };
        }) ?? []
    );
  }

  hasMasterBrands() {
    return this.getMasterBrands()?.length > 0;
  }

  getAgendaBrandId(): string {
    return (
      this.configuration.fitterBrands.find((brand) => !!brand.agenda)?.id ||
      null
    );
  }

  hasNoSlotFitterWithDelayDisplay(): boolean {
    return !!this.getConfig()?.fitterBrands?.find(
      (brand) =>
        brand?.booking?.search?.version === 'NO_SLOT' &&
        brand?.features?.includes('should-display-later')
    );
  }

  getAbTestingRate(abTestType: AbTestType): number {
    return this.configuration?.abTestingRates?.[abTestType] ?? 0;
  }
}

function getDefaultOrigin(clientAppId: ClientAppId): DossierGlobalOrigins {
  switch (clientAppId) {
    case 'customer':
      return 'SITE_WEB';
    case 'fitter':
      return 'AGENDA';
    case 'insurer':
      return 'PARTNER';
    default:
      return 'OTHER';
  }
}
